import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { map, Observable } from 'rxjs';
import { EmailDeliveryHubMetrics } from '../../../models/email-delivery-hub-metrics.model';
import { DEFAULT_EMAIL_DELIVERY_HUB_DATE_RANGE } from '../../../utils/default-date-range';
import { EmailDeliveryHubSentFilters } from '../../../models/email-delivery-hub-filters.models';
import { EmailDeliveryHubSentMetrics } from '../../../state/email-delivery-hub.model';

interface ArgsFilter {
  min_date: string;
  max_date: string;
  secondaryFilters?: ArgsFilter;
}

const INVERTED_SENTIMENT_METRICS = new Set<string>(['No Attachments', 'Bounced']);

const METRICS_FIELDS = `
  total
  bounced
  withoutAttachments: without_attachments
  delivered
  clicked
  opened
`;

const LOAD_METRICS_QUERY = gql`
  query LoadEmailDeliveryHubMetrics($main_min_date: timestamptz!, $main_max_date: timestamptz!) {
    mainData: get_outbound_email_stats(args: { min_date: $main_min_date, max_date: $main_max_date }) {
      ${METRICS_FIELDS}
    }
  }
`;

const LOAD_METRICS_WITH_COMPARISON_QUERY = gql`
  query LoadEmailDeliveryHubMetrics(
    $main_min_date: timestamptz!
    $main_max_date: timestamptz!
    $secondary_min_date: timestamptz!
    $secondary_max_date: timestamptz!
  ) {
    mainData: get_outbound_email_stats(args: { min_date: $main_min_date, max_date: $main_max_date }) {
      ${METRICS_FIELDS}
    }
    secondaryData: get_outbound_email_stats(args: { min_date: $secondary_min_date, max_date: $secondary_max_date }) {
      ${METRICS_FIELDS}
    }
  }
`;

@Injectable()
export class EmailDeliveryHubMetricsService {
  constructor(
    private readonly apollo: Apollo,
    private readonly datePipe: DatePipe,
  ) {}

  public loadSentMetrics(filters: Partial<EmailDeliveryHubSentFilters>): Observable<EmailDeliveryHubSentMetrics> {
    const args = this._buildArgsBasedOn(filters);
    const comparisonDateRange = args.secondaryFilters
      ? this._formatComparisonDateRange(args.secondaryFilters)
      : undefined;

    const hasComparison = !!args.secondaryFilters;
    const variables: Record<string, string> = {
      main_min_date: args.min_date,
      main_max_date: args.max_date,
      ...(hasComparison && {
        secondary_min_date: args.secondaryFilters.min_date,
        secondary_max_date: args.secondaryFilters.max_date,
      }),
    };

    return this.apollo
      .query<{ mainData: [EmailDeliveryHubMetrics]; secondaryData?: [EmailDeliveryHubMetrics] }>({
        query: hasComparison ? LOAD_METRICS_WITH_COMPARISON_QUERY : LOAD_METRICS_QUERY,
        variables,
      })
      .pipe(
        map(({ data }) => [data.mainData[0], data.secondaryData?.[0]]),
        map(([mainData, secondaryData]) =>
          EmailDeliveryHubMetricsService._toMetricsComponentFormat(mainData, secondaryData, comparisonDateRange),
        ),
      );
  }

  private _buildArgsBasedOn(filters: Partial<EmailDeliveryHubSentFilters>): ArgsFilter {
    const dateRange = filters.dateRange ?? { min: filters.min, max: filters.max };

    const max = dateRange.max ? new Date(dateRange.max) : new Date();
    const min = dateRange.min
      ? new Date(dateRange.min)
      : new Date(new Date(max).setDate(new Date(max).getDate() - DEFAULT_EMAIL_DELIVERY_HUB_DATE_RANGE));

    const maxPlusOneDay = new Date(max);
    maxPlusOneDay.setDate(maxPlusOneDay.getDate() + 1);

    // const emailTypes = this._getEmailTypes(filters);

    const formatDate = (date: Date): string => `${this.datePipe.transform(date, "yyyy-MM-dd'T'HH:mm:ss.SSS", 'UTC')}`;

    const baseFilters: ArgsFilter = {
      min_date: formatDate(min),
      max_date: formatDate(maxPlusOneDay),
    };

    const rangeDiff = max.getTime() - min.getTime();
    const secondaryMin = new Date(min.getTime() - rangeDiff);

    return {
      ...baseFilters,
      secondaryFilters: {
        min_date: formatDate(secondaryMin),
        max_date: formatDate(min),
      },
    };
  }

  // TODO: Keeping just in case there's discussion of needing this again. If not by 05/2026, then drop it.
  // private _getEmailTypes(filters: Partial<EmailDeliveryHubSentFilters>): string {
  //   const emailTypes = isEmpty(filters.types)
  //   || filters.types.length === 1 && filters.types[0] === ALL_EMAIL_TYPES_LABEL
  //     ? INCLUDED_EMAIL_TYPES
  //     : filters.types;
  //
  //   // The SQL function, `get_outbound_email_stats`, takes text[] which Hasura maps to _text which looks like: "{Value1,Value2,...}"
  //   return `{${emailTypes}}`
  // }

  private _formatComparisonDateRange(secondaryFilters: ArgsFilter): string {
    const maxExclusive = new Date(secondaryFilters.max_date);
    maxExclusive.setDate(maxExclusive.getDate() - 1);

    const min = this.datePipe.transform(secondaryFilters.min_date, 'MM/dd/yyyy', 'UTC');
    const max = this.datePipe.transform(maxExclusive, 'MM/dd/yyyy', 'UTC');
    return `${min} — ${max}`;
  }

  private static _toMetricsComponentFormat(
    data: EmailDeliveryHubMetrics,
    secondData?: EmailDeliveryHubMetrics,
    comparisonDateRange?: string,
  ): EmailDeliveryHubSentMetrics {
    const computeDelta = (value: number, secondaryValue?: number): number | undefined => {
      if (secondaryValue == null) {
        return undefined;
      }
      const delta = value / secondaryValue - 1;
      return Number.isFinite(delta) ? delta : undefined;
    };

    const createMetricUnit = (label: string, value: number, secondaryValue?: number) => ({
      label,
      value,
      differFromLastMetric: computeDelta(value, secondaryValue),
      invertedSentiment: INVERTED_SENTIMENT_METRICS.has(label),
    });

    return {
      total: {
        label: 'Sent emails',
        value: data.total,
        differFromLastMetric: computeDelta(data.total, secondData?.total),
      },
      bounced: data.bounced,
      metricUnits: [
        createMetricUnit('Delivered', data.delivered, secondData?.delivered),
        createMetricUnit('Bounced', data.bounced, secondData?.bounced),
        createMetricUnit('Opened', data.opened, secondData?.opened),
        createMetricUnit('Clicked', data.clicked, secondData?.clicked),
        createMetricUnit('No Attachments', data.withoutAttachments, secondData?.withoutAttachments),
      ],
      comparisonDateRange,
    };
  }
}
